import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const Loggers = () => {
  const module = useModuleFromRoute();
  const rsrc = useRsrc();
  const gridRef = useRef(null);
  const gridReadyRef = useRef(false);

  const [rows, setRows] = useState([]);
  const [levels, setLevels] = useState([]);

  const buildRows = (loggersMap) => {
    const nodeMap = new Map();

    const ensureNode = (fullName, name) => {
      if (!nodeMap.has(fullName)) {
        nodeMap.set(fullName, {
          name,
          fullName,
          configuredLevel: null,
          effectiveLevel: null,
          isDerived: true,
        });
      }
      return nodeMap.get(fullName);
    };

    Object.entries(loggersMap ?? {}).forEach(([loggerName, details]) => {
      const segments = loggerName === 'ROOT' ? ['ROOT'] : loggerName.split('.').filter(Boolean);
      let current = '';
      segments.forEach((segment, index) => {
        current = index === 0 ? segment : `${current}.${segment}`;
        ensureNode(current, segment);
      });

      const node = ensureNode(loggerName, segments[segments.length - 1] ?? loggerName);
      node.configuredLevel = details?.configuredLevel ?? null;
      node.effectiveLevel = details?.effectiveLevel ?? null;
      node.isDerived = false;
    });

    const getParentName = (fullName) => {
      if (fullName === 'ROOT') return null;
      const lastDot = fullName.lastIndexOf('.');
      return lastDot === -1 ? null : fullName.slice(0, lastDot);
    };

    const nodes = Array.from(nodeMap.values())
      .sort((a, b) => a.fullName.localeCompare(b.fullName))
      .sort((a, b) => a.fullName.split('.').length - b.fullName.split('.').length);

    nodes.forEach((node) => {
      if (node.effectiveLevel != null) return;
      const parentName = getParentName(node.fullName);
      const parent = parentName ? nodeMap.get(parentName) : null;
      if (parent?.effectiveLevel != null) {
        node.effectiveLevel = parent.effectiveLevel;
        return;
      }
      node.effectiveLevel = 'INFO';
    });

    return nodes.map((node) => ({
      name: node.name,
      fullName: node.fullName,
      configuredLevel: node.configuredLevel,
      effectiveLevel: node.effectiveLevel ?? '—',
      path: node.fullName === 'ROOT' ? 'ROOT' : node.fullName.replace(/\./g, '|'),
      isDerived: node.isDerived,
    }));
  };

  const refreshLoggers = useCallback(() => {
    if (!module) return;
    rsrc.loggers(module).then((data) => {
      setLevels(data?.levels ?? []);
      setRows(buildRows(data?.loggers));
    });
  }, [module]);

  useEffect(() => {
    refreshLoggers();
  }, [refreshLoggers]);

  useEffect(() => {
    if (gridRef.current && gridReadyRef.current) {
      gridRef.current.setDataSource(rows);
    }
  }, [rows]);

  const handleLevelChange = useCallback((loggerName, level) => {
    if (!module) return;
    rsrc.updateLogger(module, loggerName, level).then(() => {
      refreshLoggers();
    });
  }, [module, refreshLoggers]);

  const colDefs = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: 'Logger',
        autoGrouping: true,
        flex: 1,
        minWidth: 240,
      },
      {
        field: 'fullName',
        headerName: 'Full Name',
        flex: 1,
        minWidth: 260,
      },
      {
        field: 'effectiveLevel',
        headerName: 'Effective',
        width: 140,
        cellRenderer: 'loggerEffectiveCellRenderer',
      },
      {
        field: 'configuredLevel',
        headerName: 'Configured',
        width: 150,
        cellRenderer: 'loggerLevelCellRenderer',
        valueFormatter: ({ value }) => value ?? 'Inherit',
      },
    ];
  }, []);

  const standardLevels = useMemo(() => (['ERROR', 'WARN', 'INFO', 'DEBUG', 'TRACE']), []);
  const selectableLevels = useMemo(() => {
    const merged = new Set([...(levels ?? []), ...standardLevels]);
    return Array.from(merged);
  }, [levels, standardLevels]);

  const gridOptions = useMemo(() => {
    return {
      rowModelType: 'clientSide',
      treeData: true,
      groupDefaultExpanded: 0,
      columnDefs: colDefs,
      context: { levels: selectableLevels, onLevelChange: handleLevelChange },
    };
  }, [colDefs, selectableLevels, handleLevelChange]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title icon={far.faListUl} title="Loggers" />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Grid
          ref={gridRef}
          gridOptions={gridOptions}
          events={{
            onGridReady: () => {
              if (gridRef.current) {
                gridReadyRef.current = true;
                gridRef.current.setDataSource(rows);
                gridRef.current.resize();
              }
            }
          }}
        />
      </Box>
    </Box>
  );
};
